/**
 * KundenLinkPortal — Kunden-Zugangsseite über generierten Link
 *
 * Route: /portal/:token
 * Der Kunde gibt sein Passwort ein und sieht dann ein Dashboard
 * mit den angeforderten Aktionen (Dokumente hochladen, unterschreiben).
 *
 * DESIGN: Premium, minimalistisch mit Koop-Branding.
 * Issue #71
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'wouter';
import { supabase } from '@/lib/supabase';
import type { KundenLinkAktion, Kanzlei } from '@/lib/database.types';
import type { KundenProgressResponse } from '@/lib/kunden-phase';
import { KundenPhasenLeiste } from '@/components/KundenPhasenLeiste';
import AnwaltsvollmachtFormular from '@/components/AnwaltsvollmachtFormular';
import type { AnwaltsvollmachtFormData } from '@/components/AnwaltsvollmachtFormular';
import { toast } from 'sonner';
import SignaturePad from 'signature_pad';
import {
  Lock, FileUp, Pen, FileText, Shield, Scale,
  Loader2, Check, CheckCircle2, Upload,
  AlertCircle, Clock, ArrowLeft, X,
  ChevronRight, Eye,
} from 'lucide-react';

// ── Types ──

interface PortalSession {
  link: {
    id: string;
    kunde_name: string | null;
    business_unit: string;
    aktionen: KundenLinkAktion[];
    vorausgefuellte_felder: Record<string, any>;
    erledigte_aktionen: string[];
    gueltig_bis: string;
    kanzlei: Kanzlei | null;
  };
  akte: {
    id: string;
    akten_nr: string;
    titel: string;
    status: string;
    kunde_name: string | null;
    fahrzeug_kennzeichen: string | null;
    kennzeichen: string | null;
    fahrzeug_marke: string | null;
    fahrzeug_modell: string | null;
  } | null;
  dokumente: any[];
}

// Labels für Prefill-Felder — kurz und selbsterklärend für den Kunden.
const FELD_LABELS: Record<string, string> = {
  kunde_name: 'Name',
  kunde_strasse: 'Straße',
  kunde_plz: 'PLZ',
  kunde_ort: 'Ort',
  kennzeichen: 'Kennzeichen',
  schadendatum: 'Schadendatum',
  schadennummer: 'Schadennummer',
  versicherung: 'Versicherung',
  gegner_versicherung: 'Versicherung Gegner',
  mietbeginn: 'Mietbeginn',
  tagespreis: 'Tagespreis',
};

function formatPrefillValue(key: string, value: unknown): string {
  const raw = String(value ?? '');
  if (!raw) return '—';
  if ((key === 'schadendatum' || key === 'mietbeginn') && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
    try {
      return new Date(raw).toLocaleDateString('de-DE');
    } catch {
      return raw;
    }
  }
  if (key === 'tagespreis') return `${raw} €`;
  return raw;
}

const AKTION_META: Record<KundenLinkAktion, { label: string; desc: string; icon: typeof FileUp }> = {
  dokument_hochladen: { label: 'Dokumente hochladen', desc: 'Laden Sie Ihre Unterlagen hoch', icon: FileUp },
  unterschrift_auftrag: { label: 'Auftragsvereinbarung', desc: 'Auftrag zur Gutachtenerstellung', icon: Pen },
  unterschrift_abtretung: { label: 'Abtretungserklärung', desc: 'Abtretung Ihrer Ansprüche', icon: FileText },
  unterschrift_mietwagen: { label: 'Mietwagenvertrag', desc: 'Mietvertrag und Unterlagen', icon: FileText },
  unterschrift_vollmacht: { label: 'Vollmacht', desc: 'Bevollmächtigung erteilen', icon: Shield },
  unterschrift_anwaltsvollmacht: { label: 'Anwaltsvollmacht', desc: 'Vollmacht für eine Rechtsanwaltskanzlei', icon: Scale },
};

// ── Login Component ──

function PortalLogin({
  token,
  onSuccess,
  linkInfo,
}: {
  token: string;
  onSuccess: (session: PortalSession) => void;
  linkInfo: { active: boolean; expired: boolean; kunde_name: string | null } | null;
}) {
  const [passwort, setPasswort] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/api/kunden-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'validate', token, passwort }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 410) {
          setError('Dieser Link ist abgelaufen. Bitte kontaktieren Sie uns für einen neuen Zugang.');
        } else if (response.status === 401) {
          setError('Falsches Passwort. Bitte versuchen Sie es erneut.');
        } else {
          setError(data.error || 'Link nicht gefunden oder nicht mehr aktiv.');
        }
        return;
      }

      onSuccess(data);
    } catch {
      setError('Verbindungsfehler. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (linkInfo && linkInfo.expired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Link abgelaufen</h1>
            <p className="text-sm text-gray-500 mb-6">
              Dieser Zugangslink ist nicht mehr gültig.
              Bitte kontaktieren Sie uns, um einen neuen Link zu erhalten.
            </p>
            <a
              href="tel:+49123456789"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#9B1C1C] text-white rounded-xl text-sm font-bold hover:bg-[#7F1515] transition-colors"
            >
              Kontakt aufnehmen
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo / Branding */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#9B1C1C] rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Koop Portal</h1>
          <p className="text-sm text-gray-500 mt-1">Ihr persönlicher Zugang</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
          {linkInfo?.kunde_name && (
            <p className="text-center text-sm text-gray-600 mb-4">
              Willkommen, <span className="font-semibold">{linkInfo.kunde_name}</span>
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Passwort</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="password"
                  name="passwort"
                  value={passwort}
                  onChange={e => setPasswort(e.target.value)}
                  placeholder="Ihr Kennzeichen eingeben"
                  autoFocus
                  className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 focus:border-[#9B1C1C]/50 bg-gray-50"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !passwort.trim()}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#9B1C1C] text-white text-sm font-bold hover:bg-[#7F1515] transition-colors disabled:opacity-50"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Wird geprüft...</>
              ) : (
                <>Zugang öffnen</>
              )}
            </button>
          </form>

          <p className="text-center text-[10px] text-gray-400 mt-4">
            Geschützter Zugang. Ihre Daten sind sicher.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Signature Component (Unterschrift) ──

function SignaturePanel({
  dokumentTyp,
  onComplete,
  onBack,
}: {
  dokumentTyp: string;
  onComplete: (signatureDataUrl: string) => void;
  onBack: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
      });
    }

    return () => {
      signaturePadRef.current?.off();
    };
  }, []);

  const handleClear = () => {
    signaturePadRef.current?.clear();
  };

  const handleSave = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast.error('Bitte unterschreiben Sie zuerst');
      return;
    }
    const dataUrl = signaturePadRef.current.toDataURL('image/png');
    onComplete(dataUrl);
  };

  const labels: Record<string, string> = {
    unterschrift_auftrag: 'Auftragsvereinbarung',
    unterschrift_abtretung: 'Abtretungserklärung',
    unterschrift_mietwagen: 'Mietwagenvertrag',
    unterschrift_vollmacht: 'Vollmacht',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft className="w-4 h-4 text-gray-500" />
        </button>
        <div>
          <h3 className="text-base font-bold text-gray-900">{labels[dokumentTyp] || 'Unterschrift'}</h3>
          <p className="text-xs text-gray-500">Bitte unterschreiben Sie im Feld unten</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full touch-none"
          style={{ height: '240px' }}
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleClear}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <X className="w-4 h-4" /> Löschen
        </button>
        <button
          onClick={handleSave}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#9B1C1C] text-white text-sm font-bold hover:bg-[#7F1515] transition-colors"
        >
          <Check className="w-4 h-4" /> Bestätigen
        </button>
      </div>
    </div>
  );
}

// ── File Upload Component ──

function FileUploadPanel({
  linkId,
  onComplete,
}: {
  linkId: string;
  onComplete: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; size: number }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const storagePath = `kunden-links/${linkId}/${Date.now()}-${file.name}`;

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('dokumente')
          .upload(storagePath, file, { contentType: file.type, upsert: false });

        if (uploadError) {
          toast.error(`Fehler beim Hochladen von ${file.name}`);
          continue;
        }

        // Record in database (via API — RLS restricts direct anon access)
        await fetch('/api/kunden-links', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'recordDoc',
            link_id: linkId,
            name: file.name,
            dateityp: file.type,
            groesse_bytes: file.size,
            storage_pfad: storagePath,
            kategorie: 'sonstiges',
          }),
        });

        setUploadedFiles(prev => [...prev, { name: file.name, size: file.size }]);
      }

      toast.success('Dokument(e) hochgeladen');
      onComplete();
    } catch (err: any) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div
        onClick={() => fileInputRef.current?.click()}
        className="flex flex-col items-center gap-3 p-8 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#9B1C1C]/50 hover:bg-[#9B1C1C]/5 transition-colors"
      >
        {uploading ? (
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        ) : (
          <Upload className="w-8 h-8 text-gray-400" />
        )}
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700">
            {uploading ? 'Wird hochgeladen...' : 'Dokumente hochladen'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Fahrzeugschein, Fotos, Unterlagen</p>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx"
        onChange={handleFileSelect}
        className="hidden"
      />

      {uploadedFiles.length > 0 && (
        <div className="space-y-1.5">
          {uploadedFiles.map((f, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="text-xs text-gray-700 truncate flex-1">{f.name}</span>
              <span className="text-[10px] text-gray-400">{(f.size / 1024).toFixed(0)} KB</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Portal Component ──

export default function KundenLinkPortal() {
  const params = useParams<{ token: string }>();
  const token = params.token || '';

  const [linkInfo, setLinkInfo] = useState<{ active: boolean; expired: boolean; kunde_name: string | null } | null>(null);
  const [session, setSession] = useState<PortalSession | null>(null);
  const [activeAktion, setActiveAktion] = useState<KundenLinkAktion | null>(null);
  const [erledigteAktionen, setErledigteAktionen] = useState<string[]>([]);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [progress, setProgress] = useState<KundenProgressResponse | null>(null);

  // Load link info on mount
  useEffect(() => {
    if (!token) return;

    const loadLinkInfo = async () => {
      try {
        const response = await fetch('/api/kunden-links', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getLink', token }),
        });

        if (response.ok) {
          const data = await response.json();
          setLinkInfo(data);
        }
      } catch {
        // Will show login form anyway
      } finally {
        setLoadingInfo(false);
      }
    };

    loadLinkInfo();
  }, [token]);

  const handleLoginSuccess = (data: PortalSession) => {
    setSession(data);
    setErledigteAktionen(data.link.erledigte_aktionen || []);
    void loadProgress();
  };

  const loadProgress = useCallback(async () => {
    try {
      const response = await fetch('/api/kunden-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getProgress', token }),
      });
      if (response.ok) {
        const data: KundenProgressResponse = await response.json();
        setProgress(data);
      }
    } catch {
      // Progress ist optional — bei Fehler bleibt die Leiste einfach aus.
    }
  }, [token]);

  const handleAktionComplete = async (aktion: KundenLinkAktion) => {
    // Track completion
    try {
      await fetch('/api/kunden-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'trackAccess',
          link_id: session?.link.id,
          aktion,
        }),
      });
    } catch {}

    setErledigteAktionen(prev => [...prev, aktion]);
    setActiveAktion(null);
    toast.success('Erledigt!');
  };

  if (loadingInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Not authenticated → show login
  if (!session) {
    return <PortalLogin token={token} onSuccess={handleLoginSuccess} linkInfo={linkInfo} />;
  }

  const { link, akte } = session;
  const alleDone = link.aktionen.every(a => erledigteAktionen.includes(a));
  const prefillEntries = Object.entries(link.vorausgefuellte_felder || {})
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '');

  // Show Anwaltsvollmacht form (special handling for Kanzlei + Akte integration)
  if (activeAktion === 'unterschrift_anwaltsvollmacht') {
    const prefill = link.vorausgefuellte_felder || {};
    const nameParts = (prefill.kunde_name as string || link.kunde_name || '').split(' ');
    const anwaltFormData: AnwaltsvollmachtFormData = {
      vorname: nameParts.slice(0, -1).join(' ') || nameParts[0] || '',
      nachname: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
      strasse: prefill.kunde_strasse as string | undefined,
      plz: prefill.kunde_plz as string | undefined,
      ort: prefill.kunde_ort as string | undefined,
      aktenzeichen: akte?.akten_nr || undefined,
      kennzeichen: (prefill.kennzeichen as string) || akte?.fahrzeug_kennzeichen || akte?.kennzeichen || undefined,
      schadendatum: prefill.schadendatum as string | undefined,
      gegner_versicherung: prefill.gegner_versicherung as string | undefined,
      schadennummer: prefill.schadennummer as string | undefined,
    };
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-lg mx-auto">
          <AnwaltsvollmachtFormular
            kanzlei={link.kanzlei}
            formData={anwaltFormData}
            linkId={link.id}
            akteId={akte?.id as string | undefined}
            onBack={() => setActiveAktion(null)}
            onComplete={() => {
              setErledigteAktionen(prev => [...prev, 'unterschrift_anwaltsvollmacht']);
              setActiveAktion(null);
              toast.success('Vollmacht erteilt!');
              void loadProgress();
            }}
          />
        </div>
      </div>
    );
  }

  // Show signature panel (generic — for all other signature actions)
  if (activeAktion && activeAktion !== 'dokument_hochladen') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-lg mx-auto">
          <SignaturePanel
            dokumentTyp={activeAktion}
            onBack={() => setActiveAktion(null)}
            onComplete={async (signatureDataUrl) => {
              // Upload signature as PDF-like image to storage
              try {
                const blob = await (await fetch(signatureDataUrl)).blob();
                const storagePath = `kunden-links/${link.id}/${activeAktion}-${Date.now()}.png`;

                await supabase.storage
                  .from('dokumente')
                  .upload(storagePath, blob, { contentType: 'image/png', upsert: false });

                // Record in database (via API — RLS restricts direct anon access)
                await fetch('/api/kunden-links', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'recordDoc',
                    link_id: link.id,
                    name: `${AKTION_META[activeAktion]?.label || activeAktion}.png`,
                    dateityp: 'image/png',
                    groesse_bytes: blob.size,
                    storage_pfad: storagePath,
                    kategorie: activeAktion,
                  }),
                });

                handleAktionComplete(activeAktion);
              } catch {
                toast.error('Fehler beim Speichern der Unterschrift');
              }
            }}
          />
        </div>
      </div>
    );
  }

  // Show file upload panel
  if (activeAktion === 'dokument_hochladen') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setActiveAktion(null)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div>
              <h3 className="text-base font-bold text-gray-900">Dokumente hochladen</h3>
              <p className="text-xs text-gray-500">Laden Sie Ihre Unterlagen hoch</p>
            </div>
          </div>
          <FileUploadPanel
            linkId={link.id}
            onComplete={() => handleAktionComplete('dokument_hochladen')}
          />
        </div>
      </div>
    );
  }

  // ── Main Dashboard ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#9B1C1C] rounded-xl flex items-center justify-center shadow">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Koop Portal</h1>
              <p className="text-xs text-gray-500">
                {link.kunde_name ? `Willkommen, ${link.kunde_name}` : 'Ihr persönlicher Bereich'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Phasen-Leiste — Fortschritt der Akte aus Kundensicht (Issue #424) */}
        {progress && <KundenPhasenLeiste progress={progress} />}

        {/* Welcome Message */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <h2 className="text-base font-bold text-gray-900 mb-1">
            Willkommen im Koop Dashboard
          </h2>
          <p className="text-sm text-gray-600">
            Um Ihren Schadensfall schnell und unkompliziert abzuschließen,
            bitten wir Sie, die folgenden Dokumente zu bearbeiten.
          </p>

          {akte && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl border border-gray-200">
              <span className="text-xs text-gray-500">Aktenzeichen:</span>
              <span className="text-xs font-mono font-bold text-gray-800">{akte.akten_nr}</span>
              {(akte.fahrzeug_kennzeichen || akte.kennzeichen) && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-xs font-mono text-gray-600">{akte.fahrzeug_kennzeichen || akte.kennzeichen}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Vorausgefüllte Felder aus der Akte (Issue #138) */}
        {prefillEntries.length > 0 && (
          <div
            data-testid="portal-vorausgefuellte-felder"
            className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm"
          >
            <h3 className="text-sm font-bold text-gray-900 mb-3">Ihre Daten</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              {prefillEntries.map(([key, value]) => (
                <div key={key} className="flex flex-col">
                  <dt className="text-[11px] text-gray-400 uppercase tracking-wider">
                    {FELD_LABELS[key] || key}
                  </dt>
                  <dd className="text-[13px] font-medium text-gray-800 break-words">
                    {formatPrefillValue(key, value)}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="text-[11px] text-gray-400 mt-3">
              Diese Daten stammen aus Ihrer Akte und werden in den Formularen verwendet.
              Bitte melden Sie sich, falls etwas nicht stimmt.
            </p>
          </div>
        )}

        {/* Progress */}
        {link.aktionen.length > 1 && (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className="bg-emerald-500 rounded-full h-2 transition-all duration-500"
                style={{ width: `${(erledigteAktionen.length / link.aktionen.length) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-500">
              {erledigteAktionen.length}/{link.aktionen.length}
            </span>
          </div>
        )}

        {/* All done message */}
        {alleDone && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <h3 className="text-base font-bold text-emerald-800">Alles erledigt!</h3>
            <p className="text-sm text-emerald-600 mt-1">
              Vielen Dank! Wir haben alle Ihre Unterlagen erhalten.
            </p>
          </div>
        )}

        {/* Aktionen-Liste */}
        <div className="space-y-2">
          {link.aktionen.map(aktion => {
            const meta = AKTION_META[aktion];
            if (!meta) return null;
            const Icon = meta.icon;
            const done = erledigteAktionen.includes(aktion);
            const isSignature = aktion !== 'dokument_hochladen';

            return (
              <button
                key={aktion}
                onClick={() => !done && setActiveAktion(aktion)}
                disabled={done}
                className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${
                  done
                    ? 'bg-emerald-50 border-emerald-200 cursor-default'
                    : 'bg-white border-gray-200 hover:border-[#9B1C1C]/30 hover:shadow-md cursor-pointer active:scale-[0.99]'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  done ? 'bg-emerald-100' : 'bg-gray-100'
                }`}>
                  {done ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  ) : (
                    <Icon className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${done ? 'text-emerald-800' : 'text-gray-900'}`}>
                    {meta.label}
                  </p>
                  <p className={`text-xs mt-0.5 ${done ? 'text-emerald-600' : 'text-gray-500'}`}>
                    {done ? 'Erledigt' : meta.desc}
                  </p>
                </div>
                {!done && (
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-bold flex-shrink-0 ${
                    isSignature
                      ? 'bg-[#9B1C1C] text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {isSignature ? 'Unterschreiben' : 'Hochladen'}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="text-center pt-4 pb-8">
          <p className="text-[10px] text-gray-400">
            Gutachter Koop &middot; Ihr Schadenfall wird professionell bearbeitet
          </p>
        </div>
      </div>
    </div>
  );
}
